/**
 * Structured browser logger with Sentry integration.
 *
 * Features:
 * - Child loggers with bound context (requestId, component, etc.)
 * - Sentry integration: errors → captureException, warnings → captureMessage, all → breadcrumbs
 * - Production: only warn/error logged; dev: full console output
 * - GCP-compatible structured format for consistency with server logs
 * - Lazy Sentry loading: doesn't bundle Sentry until it's initialized
 *
 * Usage:
 *   import logger from '~/core/logger';
 *
 *   // Simple logging
 *   logger.info({ event: 'page_load' }, 'Page loaded');
 *   logger.error({ err }, 'Failed to load data');
 *
 *   // Child logger with bound context
 *   const log = logger.child({ requestId: 'upl_123', component: 'UploadTracker' });
 *   log.info({ phase: 'validating' }, 'Phase transition');
 */

import configuration from '~/configuration';
import { mayLoadDiagnostics } from '~/core/consent/choice';

// Lazy-loaded Sentry module reference (set by initialize-browser-sentry.ts)
// This avoids bundling Sentry in every client chunk
let sentryModule: typeof import('@sentry/react') | null = null;

/**
 * Set the Sentry module reference. Called by initialize-browser-sentry.ts
 * after Sentry is loaded and initialized.
 */
export function setSentryModule(
  sentry: typeof import('@sentry/react') | null,
): void {
  sentryModule = sentry;
}

/**
 * Get the Sentry module. Returns null if not yet loaded.
 */
function getSentry(): typeof import('@sentry/react') | null {
  return mayLoadDiagnostics() ? sentryModule : null;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  [key: string]: unknown;
}

export interface BrowserLogger {
  debug(context: LogContext, message: string): void;
  debug(context: LogContext): void;
  debug(message: string): void;
  info(context: LogContext, message: string): void;
  info(context: LogContext): void;
  info(message: string): void;
  warn(context: LogContext, message: string): void;
  warn(context: LogContext): void;
  warn(message: string): void;
  error(context: LogContext, message: string): void;
  error(context: LogContext): void;
  error(message: string): void;
  child(bindings: LogContext): BrowserLogger;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function shouldLog(level: LogLevel): boolean {
  // In production, only log warnings and errors to reduce noise
  if (configuration.production) {
    return LOG_LEVELS[level] >= LOG_LEVELS.warn;
  }
  return true;
}

function formatMessage(
  bindings: LogContext,
  context: LogContext,
  message: string,
): string {
  const component = bindings.component || context.component;
  const requestId = bindings.requestId || context.requestId;

  let prefix = '';
  if (component) prefix += `[${component}]`;
  if (requestId) prefix += ` [${requestId}]`;

  return prefix ? `${prefix} ${message}` : message;
}

function createStructuredLog(
  level: LogLevel,
  bindings: LogContext,
  context: LogContext,
  message: string,
): object {
  return {
    severity: level.toUpperCase(),
    message,
    timestamp: new Date().toISOString(),
    ...bindings,
    ...context,
  };
}

function addSentryBreadcrumb(
  level: LogLevel,
  bindings: LogContext,
  context: LogContext,
  message: string,
): void {
  // Only add breadcrumbs in production when Sentry is initialized
  if (!configuration.production) return;

  const sentry = getSentry();
  if (!sentry) return;

  try {
    sentry.addBreadcrumb({
      category: (bindings.component as string) || 'app',
      message,
      level:
        level === 'debug'
          ? 'debug'
          : level === 'info'
            ? 'info'
            : level === 'warn'
              ? 'warning'
              : 'error',
      data: { ...bindings, ...context },
    });
  } catch {
    // Sentry call failed, ignore
  }
}

function captureErrorToSentry(
  bindings: LogContext,
  context: LogContext,
  message: string,
): void {
  if (!configuration.production) return;

  const sentry = getSentry();
  if (!sentry) return;

  try {
    const error =
      context.err instanceof Error ? context.err : new Error(message);

    sentry.withScope((scope) => {
      scope.setExtras({ ...bindings, ...context });
      if (bindings.component) {
        scope.setTag('component', String(bindings.component));
      }
      if (bindings.requestId) {
        scope.setTag('requestId', String(bindings.requestId));
      }
      sentry.captureException(error);
    });
  } catch {
    // Sentry call failed, fall through to console
  }
}

function captureWarningToSentry(
  bindings: LogContext,
  context: LogContext,
  message: string,
): void {
  if (!configuration.production) return;

  const sentry = getSentry();
  if (!sentry) return;

  try {
    sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setExtras({ ...bindings, ...context });
      if (bindings.component) {
        scope.setTag('component', String(bindings.component));
      }
      if (bindings.requestId) {
        scope.setTag('requestId', String(bindings.requestId));
      }
      sentry.captureMessage(message);
    });
  } catch {
    // Sentry call failed, fall through to console
  }
}

function createLogger(bindings: LogContext = {}): BrowserLogger {
  const log = (
    level: LogLevel,
    ...args: [LogContext, string] | [LogContext] | [string]
  ): void => {
    let context: LogContext;
    let message: string;

    if (args.length === 1) {
      const arg = args[0];
      if (typeof arg === 'string') {
        // log('message')
        context = {};
        message = arg;
      } else {
        // log({ context }) - derive message from event property or use default
        context = arg;
        message = typeof context.event === 'string' ? context.event : 'log';
      }
    } else {
      // log({ context }, 'message')
      context = args[0] as LogContext;
      message = args[1] as string;
    }

    // Add breadcrumb for all levels (helps with error context)
    addSentryBreadcrumb(level, bindings, context, message);

    // Capture warnings and errors to Sentry as events
    if (level === 'warn') {
      captureWarningToSentry(bindings, context, message);
    } else if (level === 'error') {
      captureErrorToSentry(bindings, context, message);
    }

    // Console output (respects production filtering)
    if (!shouldLog(level)) return;

    const formattedMessage = formatMessage(bindings, context, message);
    const structuredLog = createStructuredLog(
      level,
      bindings,
      context,
      message,
    );

    switch (level) {
      case 'debug':
        console.debug(formattedMessage, structuredLog);
        break;
      case 'info':
        console.info(formattedMessage, structuredLog);
        break;
      case 'warn':
        console.warn(formattedMessage, structuredLog);
        break;
      case 'error':
        console.error(formattedMessage, structuredLog);
        break;
    }
  };

  return {
    debug: (...args: [LogContext, string] | [LogContext] | [string]) =>
      log('debug', ...args),
    info: (...args: [LogContext, string] | [LogContext] | [string]) =>
      log('info', ...args),
    warn: (...args: [LogContext, string] | [LogContext] | [string]) =>
      log('warn', ...args),
    error: (...args: [LogContext, string] | [LogContext] | [string]) =>
      log('error', ...args),
    child: (childBindings: LogContext) =>
      createLogger({ ...bindings, ...childBindings }),
  };
}

export const browserLogger = createLogger();

export default browserLogger;
